import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { useParams } from "react-router";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { useSnackbar } from "notistack";
import escapeRegExp from "lodash.escaperegexp";

import { Box, Fab, Tooltip, Stack } from "@mui/material";
import {
  UnfoldMoreVertical as HtmlIcon,
  InformationOutline as MetadataIcon,
  OpenInNew as LinkIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import SaveFab from "components/common/SaveFab";
import WebSiteLink from "components/common/WebSiteLink";
import ArticlePreview from "components/article/ArticlePreview";
import ArticleMetadata from "components/article/ArticleMetadata";
import MarkdownField from "components/form/MarkdownField";
import FileField from "components/form/FileField";

const ARTICLE_FRAGMENT = gql`
  fragment ArticlePage_Article on Article {
    id

    content {
      en
      fr
    }

    files {
      id
      ...FileField_file
    }

    ...Articles_ArticleMetadata_Article
  }

  ${FileField.fragments.file}
  ${ArticleMetadata.fragments.article}
`;

const QUERY = gql`
  query ($articleId: ID!) {
    article(id: $articleId) {
      id
      ...ArticlePage_Article
    }
  }

  ${ARTICLE_FRAGMENT}
`;

const MUTATION = gql`
  mutation ($articleId: ID!, $content: IntlStringInput, $files: [FileInput]) {
    UpdateArticle(id: $articleId, content: $content, files: $files) {
      article {
        id
        ...ArticlePage_Article
      }
    }
  }

  ${ARTICLE_FRAGMENT}
`;

function getFileSnippet(type, file, locale) {
  const CLEAN_REGEXP = new RegExp("^[ ]{6}([ ]{2})?", "gm");

  switch (type) {
    case "audio":
      return `
        <figure class="center">
          <div class="audio">
            <audio src="${file.url}" controls="controls"></audio>
          </div>
          <figcaption></figcaption>
        </figure>
      `.replace(CLEAN_REGEXP, "");

    case "image": {
      const alt = file.metas && file.metas.alt && file.metas.alt[locale];
      const legend =
        file.metas && file.metas.legend && file.metas.legend[locale];

      return `
        <figure class="center">
          <img src="${file.url}" alt="${alt || ""}" />
          <figcaption>${legend || ""}</figcaption>
        </figure>
      `.replace(CLEAN_REGEXP, "");
    }

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { enqueueSnackbar } = useSnackbar();
  const { articleId } = useParams();
  const [locale, setLocale] = React.useState("fr");
  const editorInstance = React.useRef();

  const [updateArticle] = useMutation(MUTATION);

  const { data, loading } = useQuery(QUERY, {
    variables: { articleId },
    fetchPolicy: "network-only",
  });

  const handleHtmlSnippet = React.useCallback(
    async (preview, file) => {
      const result = await navigator.permissions.query({
        name: "clipboard-write",
      });

      if (result.state === "granted" || result.state === "prompt") {
        const text = getFileSnippet(preview, file, locale);

        if (!text) return;

        await navigator.clipboard.writeText(text);
        enqueueSnackbar("HTML copié dans le presse papier.");
      }
    },
    [enqueueSnackbar, locale]
  );

  if (loading) return <Layout.Loading />;

  const article = data?.article;
  if (!article) return <Layout.NotFound />;

  return (
    <Form
      onSubmit={(values) =>
        updateArticle({
          variables: {
            articleId: article.id,
            ...values,
          },
        })
          .then(() => {})
          .catch(() => true)
      }
      initialValues={{
        files: article.files,
        content: Object.assign({ fr: "", en: "" }, article.content),
      }}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit, submitting, pristine, values, form }) => (
        <Box
          sx={{
            flexGrow: 1,
            flexBasis: 0,
            display: "flex",
            overflow: "hidden",
          }}
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
        >
          <Layout.Toolbar maxWidth={false}>
            <Stack spacing={2} direction="row" alignItems="center">
              <Box sx={{ flexGrow: 1 }}>
                <FileField
                  name="files"
                  uploadPath={`articles/${article.id}`}
                  isPublic
                  multiple
                  preview={({ name }) => {
                    if (name.match(/\.(png|jpe?g|gif|bmp)$/)) return "image";
                    if (name.match(/\.(mp3|ogg|wav)/)) return "audio";
                  }}
                  renderActions={({ preview, file }) => (
                    <FileField.Action
                      label="HTML"
                      icon={<HtmlIcon />}
                      onClick={() => handleHtmlSnippet(preview, file)}
                    />
                  )}
                />

                <OnChange name="files">
                  {(files, prevFiles) => {
                    files.forEach((file, idx) => {
                      const prevFile = prevFiles[idx];
                      if (!prevFile) return;

                      const regExp = new RegExp(
                        escapeRegExp(prevFile.url),
                        "g"
                      );

                      form.change(
                        "content.fr",
                        values.content.fr.replace(regExp, file.url)
                      );
                      form.change(
                        "content.en",
                        values.content.en.replace(regExp, file.url)
                      );
                    });
                  }}
                </OnChange>
              </Box>

              <Tooltip title="Passer à la version française de l’article">
                <div>
                  <Fab
                    onClick={() => setLocale("fr")}
                    disabled={locale === "fr"}
                    size="small"
                  >
                    🇫🇷
                  </Fab>
                </div>
              </Tooltip>

              <Tooltip title="Passer à la version anglaise de l’article">
                <div>
                  <Fab
                    onClick={() => setLocale("en")}
                    disabled={locale === "en"}
                    size="small"
                  >
                    🇬🇧
                  </Fab>
                </div>
              </Tooltip>

              <Tooltip title="Voir sur le site">
                <div>
                  <Fab
                    size="small"
                    component={WebSiteLink}
                    locale={locale}
                    intlTo={article.url}
                  >
                    <LinkIcon />
                  </Fab>
                </div>
              </Tooltip>

              <ArticleMetadata article={article}>
                {({ isOpen, open }) => (
                  <>
                    <SaveFab
                      onSave={handleSubmit}
                      disabled={pristine || isOpen}
                      loading={submitting}
                    />

                    <Tooltip title="Informations & métadonnées">
                      <Fab color="primary" size="small" onClick={open}>
                        <MetadataIcon />
                      </Fab>
                    </Tooltip>
                  </>
                )}
              </ArticleMetadata>
            </Stack>
          </Layout.Toolbar>

          <Box sx={{ flex: "1 0 50%", overflow: "auto" }}>
            <MarkdownField
              name={`content.${locale}`}
              editorDidMount={(editor) => (editorInstance.current = editor)}
            />
          </Box>

          <Box sx={{ flex: "1 0 50%", overflow: "auto", p: 2 }}>
            <ArticlePreview mdContent={values.content[locale]} />
          </Box>
        </Box>
      )}
    </Form>
  );
}
